/**
 * The host's connect sheet (ADR-029 §2) — drawn when a plugin asks to use an
 * outside account (`useFiasConnections().connect(provider)`, the
 * `connections-connect` client-side entity).
 *
 * Every word is the platform's: the plugin names only a provider. The person
 * either lets this app use an account the workspace already connected, or
 * connects a new one in a popup — the OAuth round-trip and the grant run on
 * host-session routes, so the plugin can never mint either. Owners and admins
 * decide for an org workspace; anyone else is told who can.
 *
 * The popup's return page posts the outcome on the `fias-connections`
 * BroadcastChannel; the sheet then finds the connection the server granted to
 * this app in the same transaction it was saved.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api, type ConnectionDto } from '@/lib/api';
import { useConsentArmDelay } from '@/lib/hooks/useConsentArmDelay';
import {
  useConnectionProviders,
  useGrantConnection,
  useHolderConnections,
} from '@/lib/hooks/useConnections';
import { openConnectPopup, watchConnectPopupClosed } from '@/lib/connect-popup';
import type { ConnectionsConnectResult } from '@fias/client-entities';

/** The channel the OAuth return page (`/connections/complete`) posts on. */
export const CONNECTIONS_BROADCAST_CHANNEL = 'fias-connections';

export interface ConnectAccountSheetProps {
  open: boolean;
  archeId: string;
  archeName: string | null;
  provider: string;
  /** The workspace the app is open in — the Holder the account belongs to. */
  holderId: string;
  holderLabel: string;
  onResolve: (result: ConnectionsConnectResult) => void;
}

const FAILURE_COPY: Record<string, string> = {
  PROVIDER_DENIED: 'The account was not connected.',
  STATE_INVALID: 'That sign-in took too long. Try again.',
  SESSION_MISMATCH: 'Finish connecting in the same browser you are signed in to Fias with.',
  SAVE_FAILED: 'Fias could not save the account. Try again.',
  NOT_ALLOWED: 'You can no longer manage this workspace’s accounts.',
  EXCHANGE_FAILED: 'The provider did not finish connecting the account. Try again.',
  ARCHE_NOT_GRANTABLE: 'The account connected, but this app cannot be given it.',
  APP_NOT_FOUND: 'This app is no longer available to you, so nothing was connected.',
  ISSUER_MISMATCH: 'The sign-in came back from the wrong place, so nothing was connected.',
  INSUFFICIENT_CREDITS: 'Not enough credits to look up the account. Add credits and try again.',
};

function accountLabel(connection: ConnectionDto): string {
  if (connection.accountHandle) return `@${connection.accountHandle}`;
  return connection.accountDisplayName ?? connection.providerDisplayName;
}

export function ConnectAccountSheet({
  open,
  archeId,
  archeName,
  provider,
  holderId,
  holderLabel,
  onResolve,
}: ConnectAccountSheetProps) {
  const providers = useConnectionProviders();
  const connections = useHolderConnections(holderId);
  const grant = useGrantConnection(holderId);
  const [phase, setPhase] = useState<'choose' | 'connecting'>('choose');
  const [problem, setProblem] = useState<string | null>(null);
  const armed = useConsentArmDelay(phase);
  const settled = useRef(false);
  const popupRef = useRef<Window | null>(null);

  const app = archeName ?? 'This app';
  const providerConfig = providers.data?.find((p) => p.provider === provider);
  const accounts = useMemo(
    () => (connections.data?.connections ?? []).filter((c) => c.provider === provider),
    [connections.data, provider],
  );

  const resolve = (result: ConnectionsConnectResult) => {
    if (settled.current) return;
    settled.current = true;
    onResolve(result);
  };

  // An account this app was already given resolves at once — nothing to ask —
  // unless the grant is stale: the account gained scopes since (its grant is
  // pinned to what the person agreed to), or the platform now needs scopes
  // the account lacks. Then the person is asked again: "Use" re-grants,
  // "Reconnect" connects it again with the scopes needed.
  useEffect(() => {
    const already = accounts.find(
      (c) =>
        c.status === 'active' &&
        c.needsReconnect !== true &&
        c.apps.some((a) => a.archeId === archeId && a.current !== false),
    );
    if (already && phase === 'choose') {
      resolve({ connectionId: already.connectionId, provider });
    }
  }, [accounts, archeId, provider, phase]);

  // The popup reports back on a BroadcastChannel; on success the server has
  // already granted the new connection to this app.
  useEffect(() => {
    if (phase !== 'connecting' || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(CONNECTIONS_BROADCAST_CHANNEL);
    channel.onmessage = async (event: MessageEvent) => {
      const data = event.data as {
        status?: string;
        provider?: string;
        reason?: string;
        connectionId?: string;
      };
      if (data.status === 'connected' && data.provider === provider) {
        const refreshed = await connections.refetch();
        // The account the server just saved and granted, by id.
        const granted = (refreshed.data?.connections ?? []).find(
          (c) =>
            c.connectionId === data.connectionId &&
            c.provider === provider &&
            c.apps.some((a) => a.archeId === archeId),
        );
        if (granted) {
          resolve({ connectionId: granted.connectionId, provider });
          return;
        }
        setProblem('The account connected, but this app was not given it.');
      } else if (data.status === 'failed') {
        setProblem(FAILURE_COPY[data.reason ?? ''] ?? 'The account was not connected.');
      }
      setPhase('choose');
    };
    return () => channel.close();
  }, [phase, provider, archeId]);

  // A window closed before it reported back returns the sheet to its choices.
  useEffect(() => {
    const popup = popupRef.current;
    if (phase !== 'connecting' || !popup) return;
    return watchConnectPopupClosed(popup, () => {
      setProblem('The window closed before the account connected.');
      setPhase('choose');
    });
  }, [phase]);

  const connectNew = async () => {
    setProblem(null);
    const popup = openConnectPopup();
    if (!popup) {
      setProblem('Allow pop-ups for Fias to connect an account.');
      return;
    }
    try {
      const { authorizationUrl } = await api.startConnectionOAuth({
        provider,
        holderId,
        archeId,
      });
      popup.location.href = authorizationUrl;
      popupRef.current = popup;
      setPhase('connecting');
    } catch {
      popup.close();
      setProblem('Could not start connecting the account. Try again.');
    }
  };

  const chooseExisting = async (connection: ConnectionDto) => {
    setProblem(null);
    try {
      await grant.mutateAsync({ connectionId: connection.connectionId, archeId });
      resolve({ connectionId: connection.connectionId, provider });
    } catch {
      setProblem('Could not let this app use that account. Try again.');
    }
  };

  const loading = providers.isLoading || connections.isLoading;
  const canManage = connections.data?.canManage ?? false;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && resolve({ canceled: true })}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" aria-hidden />
            {providerConfig
              ? `Let ${app} use a ${providerConfig.displayName} account?`
              : 'Connect an account'}
          </DialogTitle>
          <DialogDescription>
            {providerConfig
              ? `${app} will be able to act as the account you choose, in ${holderLabel}. It never sees your password or sign-in, it asks you before it posts, and you can take its access away in Workspace Controls.`
              : 'Fias cannot connect that kind of account.'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-6" role="status">
            <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading" />
          </div>
        )}

        {!loading && providerConfig && !canManage && (
          <p className="text-sm text-muted-foreground">
            Only the owners and admins of {holderLabel} can connect its accounts or let apps use
            them.
          </p>
        )}

        {!loading && providerConfig && canManage && phase === 'choose' && (
          <div className="space-y-2">
            {accounts
              .filter((c) => c.status === 'active')
              .map((connection) => (
                <Button
                  key={connection.connectionId}
                  variant="outline"
                  className="w-full justify-between"
                  disabled={!armed || grant.isPending}
                  onClick={() =>
                    void (connection.needsReconnect ? connectNew() : chooseExisting(connection))
                  }
                >
                  <span>
                    {connection.needsReconnect ? 'Reconnect' : 'Use'} {accountLabel(connection)}
                  </span>
                </Button>
              ))}
            <Button className="w-full" disabled={!armed} onClick={() => void connectNew()}>
              Connect {accounts.length > 0 ? 'another' : 'a'} {providerConfig.displayName} account
            </Button>
          </div>
        )}

        {phase === 'connecting' && (
          <p className="text-sm text-muted-foreground" role="status">
            Finish connecting in the window that opened…
          </p>
        )}

        {problem && (
          <p className="text-sm text-destructive" role="alert">
            {problem}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => resolve({ canceled: true })}>
            {providerConfig && canManage ? 'Cancel' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
